import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Lightbox({ images, currentIndex, onClose, onPrev, onNext }) {
  const startX = useRef(0);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
    };
  }, [onClose, onPrev, onNext]);

  if (currentIndex === null || currentIndex === undefined) return null;

  const handleTouchStart = (e) => { startX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    const diff = startX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) onNext(); else onPrev();
    }
  };

  return createPortal(
    <div
      data-testid="lightbox-overlay"
      className="flex items-center justify-center"
      style={{ position: "fixed", inset: 0, zIndex: 9999, backgroundColor: "rgba(0,0,0,0.92)" }}
      onClick={onClose}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Close */}
      <button
        data-testid="lightbox-close-btn"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); onClose(); }}
        className="absolute top-4 right-4 rounded-full bg-white/15 flex items-center justify-center text-white active:bg-white/30"
        style={{ width: 44, height: 44, zIndex: 10 }}
      >
        <X size={22} />
      </button>

      {/* Prev */}
      {images.length > 1 && (
        <button
          data-testid="lightbox-prev-btn"
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); onPrev(); }}
          className="absolute left-3 md:left-8 rounded-full bg-white/15 flex items-center justify-center text-white active:bg-white/30"
          style={{ width: 44, height: 44, zIndex: 10, top: "50%", transform: "translateY(-50%)" }}
        >
          <ChevronLeft size={22} />
        </button>
      )}

      {/* Image */}
      <img
        data-testid="lightbox-image"
        src={images[currentIndex]?.src}
        alt={images[currentIndex]?.alt || ""}
        style={{ maxWidth: "90vw", maxHeight: "85vh", objectFit: "contain", borderRadius: "12px" }}
        onClick={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
      />

      {/* Next */}
      {images.length > 1 && (
        <button
          data-testid="lightbox-next-btn"
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); onNext(); }}
          className="absolute right-3 md:right-8 rounded-full bg-white/15 flex items-center justify-center text-white active:bg-white/30"
          style={{ width: 44, height: 44, zIndex: 10, top: "50%", transform: "translateY(-50%)" }}
        >
          <ChevronRight size={22} />
        </button>
      )}

      {/* Counter */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-sm">
        {currentIndex + 1} / {images.length}
      </div>
    </div>,
    document.body
  );
}
